import type { Tier } from '../model/types.ts';

export interface EnemyTemplate {
  name: string;
  hp: number;
  attack: number;
  armor: number;
  attackEveryTicks: number;
  role: 'trash' | 'elite' | 'boss';
  /** Ironhide-style stacking armor. DR = min(cap, stacks * dr). */
  armorStacks?: number;
  armorStackGrowth?: number;
  armorStackEveryTicks?: number;
  stackDr?: number;
  stackDrCap?: number;
}

export interface WaveDef {
  enemies: EnemyTemplate[];
}

export interface StageDef {
  id: number;
  zoneId: number;
  zoneName: string;
  name: string;
  tier: Tier;
  waves: WaveDef[];
  limitTicks: number;
  goldPerKill: number;
  clearGold: number;
  clearXp: number;
  /** Forge dust granted on a clear. Earned, never sold. */
  clearDust: number;
  dropRolls: number;
  recommendedPower: number;
  threat: string[];
  isWall: boolean;
  wallHint?: string;
}

function foe(
  name: string,
  hp: number,
  attack: number,
  armor: number,
  role: EnemyTemplate['role'] = 'trash',
  every = 8,
): EnemyTemplate {
  return { name, hp, attack, armor, attackEveryTicks: every, role };
}

function tide(count: number, template: EnemyTemplate): EnemyTemplate[] {
  return Array.from({ length: count }, () => ({ ...template }));
}

export const STAGES: StageDef[] = [
  {
    id: 1,
    zoneId: 1,
    zoneName: 'Frayed Verge',
    name: 'Threadbare Scraps',
    tier: 1,
    waves: [
      { enemies: [foe('Fray Mite', 36, 4, 0), foe('Fray Mite', 36, 4, 0)] },
      { enemies: [foe('Fray Mite', 42, 5, 0), foe('Fray Mite', 42, 5, 0), foe('Fray Mite', 36, 4, 0)] },
    ],
    limitTicks: 240,
    goldPerKill: 2,
    clearGold: 8,
    clearXp: 16,
    clearDust: 1,
    dropRolls: 1,
    recommendedPower: 40,
    threat: ['Trash'],
    isWall: false,
  },
  {
    id: 2,
    zoneId: 1,
    zoneName: 'Frayed Verge',
    name: 'Loom Rats',
    tier: 1,
    waves: [
      { enemies: [foe('Loom Rat', 70, 6, 2), foe('Loom Rat', 70, 6, 2)] },
      { enemies: [foe('Loom Rat', 90, 7, 4, 'elite', 7), foe('Fray Mite', 40, 5, 0)] },
    ],
    limitTicks: 280,
    goldPerKill: 3,
    clearGold: 10,
    clearXp: 18,
    clearDust: 1,
    dropRolls: 1,
    recommendedPower: 70,
    threat: ['Light armor'],
    isWall: false,
  },
  {
    id: 3,
    zoneId: 1,
    zoneName: 'Frayed Verge',
    name: 'Hollowed Patrol',
    tier: 1,
    waves: [
      { enemies: [foe('Hollowed', 110, 8, 8), foe('Hollowed', 110, 8, 8)] },
      { enemies: [foe('Hollowed', 140, 10, 10, 'elite', 7), foe('Hollowed', 100, 8, 6)] },
    ],
    limitTicks: 320,
    goldPerKill: 3,
    clearGold: 12,
    clearXp: 22,
    clearDust: 1,
    dropRolls: 1,
    recommendedPower: 100,
    threat: ['Armor'],
    isWall: false,
  },
  {
    id: 4,
    zoneId: 1,
    zoneName: 'Frayed Verge',
    name: 'Spindle Warden',
    tier: 1,
    waves: [
      { enemies: [foe('Warden Hound', 120, 9, 6), foe('Warden Hound', 120, 9, 6)] },
      {
        enemies: [foe('Spindle Warden', 520, 14, 22, 'boss', 6)],
      },
    ],
    limitTicks: 360,
    goldPerKill: 4,
    clearGold: 20,
    clearXp: 30,
    clearDust: 2,
    dropRolls: 1,
    recommendedPower: 140,
    threat: ['Boss', 'Armor'],
    isWall: false,
  },
  {
    id: 5,
    zoneId: 2,
    zoneName: 'Rope Span',
    name: 'Bridge Gnawers',
    tier: 2,
    waves: [
      { enemies: [foe('Gnawer', 160, 12, 10), foe('Gnawer', 160, 12, 10)] },
      { enemies: [foe('Gnawer', 180, 13, 12), foe('Gnawer', 150, 11, 8), foe('Gnawer', 150, 11, 8)] },
    ],
    limitTicks: 320,
    goldPerKill: 4,
    clearGold: 16,
    clearXp: 24,
    clearDust: 2,
    dropRolls: 1,
    recommendedPower: 180,
    threat: ['Armor'],
    isWall: false,
  },
  {
    id: 6,
    zoneId: 2,
    zoneName: 'Rope Span',
    name: 'Chain Thralls',
    tier: 2,
    waves: [
      { enemies: [foe('Chain Thrall', 200, 14, 16), foe('Chain Thrall', 200, 14, 16)] },
      { enemies: [foe('Chain Thrall', 240, 16, 18, 'elite', 6), foe('Gnawer', 140, 12, 8)] },
    ],
    limitTicks: 340,
    goldPerKill: 5,
    clearGold: 18,
    clearXp: 26,
    clearDust: 2,
    dropRolls: 1,
    recommendedPower: 220,
    threat: ['Armor'],
    isWall: false,
  },
  {
    id: 7,
    zoneId: 2,
    zoneName: 'Rope Span',
    name: 'Span Sentinels',
    tier: 2,
    waves: [
      { enemies: [foe('Sentinel', 260, 16, 22, 'elite', 7), foe('Sentinel', 220, 14, 18)] },
      { enemies: [foe('Sentinel', 280, 17, 24, 'elite', 6), foe('Chain Thrall', 180, 14, 12)] },
    ],
    limitTicks: 360,
    goldPerKill: 5,
    clearGold: 20,
    clearXp: 28,
    clearDust: 2,
    dropRolls: 1,
    recommendedPower: 260,
    threat: ['Heavy armor'],
    isWall: false,
  },
  {
    id: 8,
    zoneId: 2,
    zoneName: 'Rope Span',
    name: 'The Ropekeeper',
    tier: 2,
    waves: [
      { enemies: [foe('Rope Wisp', 180, 14, 8), foe('Rope Wisp', 180, 14, 8)] },
      { enemies: [foe('The Ropekeeper', 980, 18, 36, 'boss', 6)] },
    ],
    limitTicks: 400,
    goldPerKill: 6,
    clearGold: 28,
    clearXp: 36,
    clearDust: 3,
    dropRolls: 1,
    recommendedPower: 300,
    threat: ['Boss', 'Heavy armor'],
    isWall: false,
  },
  {
    id: 9,
    zoneId: 3,
    zoneName: 'Ironhide Gate',
    name: 'Plate Scrapers',
    tier: 3,
    waves: [
      { enemies: [foe('Plate Scraper', 320, 18, 30), foe('Plate Scraper', 320, 18, 30)] },
      { enemies: [foe('Plate Scraper', 360, 20, 34, 'elite', 6), foe('Rivet Mite', 140, 12, 8)] },
    ],
    limitTicks: 360,
    goldPerKill: 6,
    clearGold: 22,
    clearXp: 42,
    clearDust: 3,
    dropRolls: 1,
    recommendedPower: 340,
    threat: ['Heavy armor'],
    isWall: false,
  },
  {
    id: 10,
    zoneId: 3,
    zoneName: 'Ironhide Gate',
    name: 'Ironhide Bulwark',
    tier: 3,
    waves: [
      {
        enemies: [
          {
            name: 'Ironhide Bulwark',
            hp: 9600,
            attack: 14,
            armor: 0,
            attackEveryTicks: 6,
            role: 'boss',
            armorStacks: 3,
            armorStackGrowth: 2,
            armorStackEveryTicks: 20,
            stackDr: 0.14,
            stackDrCap: 0.9,
          },
        ],
      },
    ],
    limitTicks: 360,
    goldPerKill: 8,
    clearGold: 40,
    clearXp: 48,
    clearDust: 4,
    dropRolls: 1,
    recommendedPower: 420,
    threat: ['Wall', 'Stacking armor', 'Armor-break'],
    isWall: true,
    wallHint: "This foe's armor thickens every 5 s. The wall tests armor-break or true damage.",
  },
  {
    id: 11,
    zoneId: 6,
    zoneName: 'Mite Gallery',
    name: 'Swarm Tide',
    tier: 3,
    waves: [{ enemies: tide(40, foe('Fray Mite', 56, 40, 0, 'trash', 2)) }],
    limitTicks: 360,
    goldPerKill: 2,
    clearGold: 36,
    clearXp: 40,
    clearDust: 3,
    dropRolls: 1,
    recommendedPower: 480,
    threat: ['Wall', 'Swarm', 'Area damage'],
    isWall: true,
    wallHint: 'A tide of weak foes arrives together. The wall tests area damage.',
  },
  {
    id: 12,
    zoneId: 9,
    zoneName: 'Last Bell',
    name: 'Enrage Clock',
    tier: 3,
    waves: [{ enemies: [foe('The Last Bell', 3300, 130, 1, 'boss', 8)] }],
    limitTicks: 360,
    goldPerKill: 10,
    clearGold: 48,
    clearXp: 56,
    clearDust: 4,
    dropRolls: 1,
    recommendedPower: 560,
    threat: ['Wall', 'Enrage 90s', 'Tank and support'],
    isWall: true,
    wallHint: 'This foe wipes the field at 90 s. The wall tests tank and support builds.',
  },
];

export const LAST_STAGE_ID = STAGES[STAGES.length - 1]!.id;

export const IRONHIDE_STAGE_ID = 10;
export const SWARM_STAGE_ID = 11;
export const ENRAGE_STAGE_ID = 12;

/** First three progression walls (§3.7). Zones 3, 6, and 9. The stages between them are not in this slice. */
export const PROGRESSION_WALLS = [IRONHIDE_STAGE_ID, SWARM_STAGE_ID, ENRAGE_STAGE_ID] as const;

/** Level used when the balance sheet scores these gates. */
export const WALL_LEVEL = 12;

/** Intended level floors. A braid below the floor should not clear that wall. */
export const WALL_LEVEL_FLOORS: Record<(typeof PROGRESSION_WALLS)[number], number> = {
  [IRONHIDE_STAGE_ID]: 7,
  [SWARM_STAGE_ID]: 9,
  [ENRAGE_STAGE_ID]: 7,
};

/**
 * Extra HP and attack for each level under the floor.
 * At the floor and above, the written stage stats are used as-is.
 */
export const WALL_UNDERLEVEL_STEP = 1;

export function wallStatMultiplier(stageId: number, level: number): number {
  const floor = WALL_LEVEL_FLOORS[stageId as keyof typeof WALL_LEVEL_FLOORS];
  if (floor === undefined || !Number.isFinite(level) || level >= floor) return 1;
  return 1 + (floor - level) * WALL_UNDERLEVEL_STEP;
}

export function getStage(id: number): StageDef {
  const stage = STAGES.find((s) => s.id === id);
  if (!stage) throw new Error(`Unknown stage ${id}`);
  return stage;
}
